import { Ref, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useClipboard } from '@vueuse/core';

import { useSettingsStore } from '@/store/useSettingsStore';
import { useUserStore } from '@/store/useUserStore';
import { useOrganizationStore } from '@/store/useOrganizationStore';
import { useEntitlementsStore } from '@/store/useEntitlementsStore';
import { useVersionsStore } from '@/modules/viewer/store/useVersionsStore';
import { useViewerItemStore, ItemSharingMode, ItemType } from '@/modules/viewer/store/useViewerItemStore';

import { useConfirmModal, useModal, ModalOptions } from '@/storybook/modal/useModal';
import { useFeatures } from '@/composables/useFeatures';
import { useToast, TOAST_TYPE } from '@/composables/useToast';
import { useAuthModals } from '@/composables/useAuthModals';
import { useVersionsModal } from '@/composables/useVersionsModal';
import { useVideoMergeModal } from '@/composables/useVideoMergeModal';
import { TrackClickEvents, TrackEvents, useViewerTrack } from '@/modules/viewer/composables/useViewerTrack';
import { useDebug, DEBUG_NAMESPACE } from '@/composables/useDebug';
import { useAuthActions } from '@/modules/viewer/composables/useAuthActions';

import { useViewerModals } from '@/modules/viewer/composables/useViewerModals';
import { useAddToCollectionModal } from '@/composables/useAddToCollectionsModal';
import { useUser } from '@/modules/viewer/composables/useUser';
import ShareModal from '@/modules/viewer/components/share/ShareModal.vue';
import ButtonModal from '@/modules/viewer/components/button/ButtonModal.vue';
import CtaUpsellingModal from '@/modules/viewer/components/button/CtaUpsellingModal.vue';
import { useTemplatesModal } from '@/modules/viewer/composables/useTemplatesModal';
import { useSubheaderTabs, SubheaderTabs } from './useSubheaderTabs';
import { useCallToActionStore } from '../store/useCallToActionStore';

const CUSTOMIZE_PAGE_URL = `/organizations/{{orgId}}/settings/customize`;

export const useItemActions = () => {
  const router = useRouter();
  const route = useRoute();

  const settingsStore = useSettingsStore();
  const userStore = useUserStore();
  const viewerItemStore = useViewerItemStore();
  const versionsStore = useVersionsStore();
  const ctaStore = useCallToActionStore();
  const { showAuthModal } = useAuthActions();

  const { isOrgUserViewOnly, isLoggedIn } = storeToRefs(userStore);
  const {
    id,
    shareLink,
    downloadLink,
    sharing,
    password,
    commentsDisabled,
    reactionsDisabled,
    type: itemType,
    isEditProcessing,
  } = storeToRefs(viewerItemStore);
  const { id: organizationId } = storeToRefs(useOrganizationStore());
  const entitlementsStore = useEntitlementsStore();
  const { selectTab } = useSubheaderTabs();

  const { copy, copied } = useClipboard({ source: shareLink as Ref });
  const { getFeature } = useFeatures();
  const { toast, success } = useToast();

  const { showSignInModal } = useAuthModals();
  const { showVideoMergeModal } = useVideoMergeModal();

  const { showVersionsModal } = useVersionsModal();
  const { trackItemOwner, trackClick } = useViewerTrack();
  const { debug } = useDebug(DEBUG_NAMESPACE.VIEWER_ITEM);
  const { showViewOnlyUserModal } = useViewerModals();
  const { isCurrentItemOwner } = useUser();
  const { isDirty: isCtaDirty } = storeToRefs(ctaStore);
  const { showTemplatesModal } = useTemplatesModal();

  const downloadAction = () => {
    // @ts-ignore
    window.location.assign(downloadLink.value);

    // if the origin domain is not share.getcloudapp.com proceed with auth modal logic
    if (showAuthModal()) return;

    toast(`Did you know you don't have to download to share? Just click the button and share anywhere`, TOAST_TYPE.INFO, {
      title: 'Download starting...',
      icon: 'general.download',
      actions: [
        {
          title: 'Copy Link',
          icon: 'general.copy',
          handler: () => copy(),
        },
      ],
    });

    trackItemOwner(TrackEvents.VIEWER_DOWNLOAD_ITEM_CLICK);
  };

  const copyCurrentItem = (duplicate: boolean = false) => {
    return viewerItemStore.copyCurrentItem({ duplicate });
  };

  const saveAction = async () => {
    trackItemOwner(TrackEvents.VIEWER_SAVE_ITEM_CLICK);

    if (!isLoggedIn.value) {
      if (id.value) {
        return showSignInModal(settingsStore.getCopyItemUrl(id.value));
      } else {
        debug('Missing item id value when copy item');
        return;
      }
    }

    if (isOrgUserViewOnly.value) {
      return showViewOnlyUserModal();
    }

    await copyCurrentItem();
  };

  const duplicateAction = async () => {
    // @todo tracking
    await copyCurrentItem(true);
  };

  const openShareModalAction = (modalOptions?: ModalOptions) => {
    if (isOrgUserViewOnly.value) {
      return showViewOnlyUserModal();
    }

    trackItemOwner(TrackEvents.VIEWER_SHARE_ITEM_MODAL_OPEN);

    const { open } = useModal(ShareModal, {
      ...modalOptions,
      layoutProps: {
        onClosed: () => {
          if (isCurrentItemOwner.value && sharing.value === ItemSharingMode.PASSWORD && !password.value) {
            toast(`Set a password to share the item.\nOtherwise, only you will be able to view it.`, TOAST_TYPE.WARNING, {
              title: 'Your item is hidden from others...',
              icon: 'general.shieldKey',
              actions: [
                {
                  title: 'Set Password',
                  handler: () => open(),
                },
              ],
            });
          }
        },
      },
    });

    open();
  };

  const openMergeVideoModalAction = () => {
    if (isOrgUserViewOnly.value) {
      return showViewOnlyUserModal();
    }
    if (!itemType.value || [ItemType.VIDEO].indexOf(itemType.value) === -1) {
      debug(`Merge Video unsupported type: ${itemType.value}`);
      return;
    }

    if (isEditProcessing.value) {
      return toast('Please wait for the current operation to complete.', TOAST_TYPE.INFO);
    }

    showVideoMergeModal();
  };

  const openVersionsModalAction = () => {
    if (isOrgUserViewOnly.value) {
      return showViewOnlyUserModal();
    }
    if (!isCurrentItemOwner.value) {
      debug('Item versions unsupported: not owner');
      return;
    }
    if (!itemType.value || [ItemType.VIDEO, ItemType.IMAGE].indexOf(itemType.value) === -1) {
      debug(`Item versions unsupported type: ${itemType.value}`);
      return;
    }

    if (entitlementsStore.can('versioning')) {
      versionsStore.load(id.value);
    }
    showVersionsModal();
  };

  const copyAction = () => {
    if (shareLink.value) {
      copy();
    }
  };

  const toggleFavoriteAction = () => {
    if (isOrgUserViewOnly.value) {
      return showViewOnlyUserModal();
    }

    trackItemOwner(TrackEvents.ITEM_ADD_TO_FAVORITES);
    viewerItemStore.toggleFavorite(id.value);
  };

  const toggleCommentsDisabledAction = () => {
    if (isOrgUserViewOnly.value) {
      return showViewOnlyUserModal();
    }

    viewerItemStore.toggleFeatureDisabled(id.value, { comments_disabled: !commentsDisabled.value }).then(() => {
      if (!commentsDisabled.value) {
        selectTab(SubheaderTabs.DISCUSSION);
      }
    });
  };

  const toggleReactionsDisabledAction = () => {
    if (isOrgUserViewOnly.value) {
      return showViewOnlyUserModal();
    }

    viewerItemStore.toggleFeatureDisabled(id.value, { reactions_disabled: !reactionsDisabled.value });
  };

  const addButtonAction = () => {
    if (!isCurrentItemOwner.value) {
      return;
    }

    if (isOrgUserViewOnly.value) {
      return showViewOnlyUserModal();
    }

    if (!entitlementsStore.can('callToAction')) {
      const { open: openCtaUpsellingModal } = useModal(CtaUpsellingModal);
      return openCtaUpsellingModal();
    }

    const { open, close } = useModal(ButtonModal, {
      hideClose: true,
      hideOverlay: false,
      clickToClose: false,
      onClickOutside: () => {
        if (!isCtaDirty.value) {
          return close();
        }

        const { open: openDiscardModal } = useConfirmModal({
          title: 'Cancel & discard changes?',
          message: 'All changes will be permanently lost.',
          confirmText: 'Discard and close',
          cancelText: 'Continue editing',
        });

        openDiscardModal().then((confirmed: boolean) => {
          if (confirmed) {
            ctaStore.restoreCta();
            return close();
          }
        });
      },
    });

    return open();
  };

  const applyTemplateAction = () => {
    if (isOrgUserViewOnly.value) {
      return showViewOnlyUserModal();
    }
    if (!isCurrentItemOwner.value) {
      debug('Apply template: not owner');
      return;
    }

    return showTemplatesModal();
  };

  const customizePageAction = () => {
    trackClick(TrackClickEvents.ITEM_CUSTOMIZE_PAGE);

    if (isOrgUserViewOnly.value) {
      return showViewOnlyUserModal();
    }

    if (organizationId && organizationId.value) {
      window.location.pathname = CUSTOMIZE_PAGE_URL.replace('{{orgId}}', organizationId.value);
    }
  };

  const moveToTrashAction = () => {
    const { open } = useConfirmModal({
      title: 'Move to trash',
      message: 'Are you sure you want to move this item to trash?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
    });

    open().then((confirmed: boolean) => {
      if (!confirmed) {
        return Promise.resolve();
      }

      return viewerItemStore.moveToTrash().then(() => {
        const msg = 'Item scheduled for archival. It will be deleted in 30 days.';
        success(msg);
        try {
          localStorage.setItem('vue-flash', JSON.stringify({ type: 'success', message: msg }));
        } catch {
          console.warn('Local storage is not available');
        }

        // @todo for now we are using window here to move to /

        // @ts-ignore
        window.location.href = '/';
      });
    });
  };

  const annotateAction = () => {
    if (isOrgUserViewOnly.value) {
      return showViewOnlyUserModal();
    }

    router.push({
      name: 'annotations',
      params: {
        ...route.params,
      },
      query: {
        ...route.query,
      },
    });
  };

  const addToCollectionAction = () => {
    if (isOrgUserViewOnly.value) {
      return showViewOnlyUserModal();
    }
    if (!isCurrentItemOwner.value) {
      debug('Add to collection: not owner');
      return;
    }
    if (!id.value) {
      return Promise.reject(new Error('Missing item id value'));
    }

    const { open } = useAddToCollectionModal(id.value);
    return open();
  };

  watch(copied, res => {
    if (!res) {
      return;
    }

    success('Share link copied to clipboard', { icon: 'general.copy' });
    trackItemOwner(TrackEvents.VIEWER_COPY_LINK_CLICK);
  });

  const captureThumbnailAction = async (timestamp: number) => {
    const roundedTimestamp = Math.round(timestamp);

    // Set capturing state
    viewerItemStore.setThumbnailCapturing(true);

    try {
      // Make API call to update thumbnail timestamp
      await viewerItemStore.updateThumbnailTimestamp(roundedTimestamp);

      // Update store with captured timestamp
      viewerItemStore.captureThumbnail(roundedTimestamp);

      // Show success message
      success(`Thumbnail captured at ${roundedTimestamp} seconds`, { icon: 'general.camera' });

      return { success: true, timestamp: roundedTimestamp };
    } catch (error) {
      toast('Failed to capture thumbnail', TOAST_TYPE.ERROR);
      throw error;
    } finally {
      // Reset capturing state
      viewerItemStore.setThumbnailCapturing(false);
    }
  };

  return {
    addButtonAction,
    annotateAction,
    applyTemplateAction,
    addToCollectionAction,
    captureThumbnailAction,
    copyAction,
    customizePageAction,
    downloadAction,
    duplicateAction,
    moveToTrashAction,
    openShareModalAction,
    openMergeVideoModalAction,
    openVersionsModalAction,
    saveAction,
    toggleCommentsDisabledAction,
    toggleFavoriteAction,
    toggleReactionsDisabledAction,
  };
};
